/**
 * Shared state shape + internal helpers for the turn machine. Not the public
 * entry point — import transitions from `GameState.ts`, which re-exports this
 * module's types. Split out of `GameState.ts` only to keep both files under the
 * 200-line cap; the two transition files import from here to avoid a cycle.
 *
 * Concealed tiles live in `hands[seat]`; called tiles live in `melds[seat]`
 * (never both). A seat at rest holds `13 − 3·meldCount` concealed tiles; after
 * a draw or a claim it holds one more and must discard. `drawnTile` is a UI
 * pointer at the just-drawn copy — it is already inside `hands[turn]`, so tile
 * conservation (136 total across hands + melds + discards + wall + dead wall)
 * always holds. `deadWall` is empty whenever `ruleset.kongEnabled` is false.
 */

import { SEAT_COUNT, type Meld, type Seat, type Tile } from './types.js';
import type { Ruleset } from './ruleset/types.js';

/** The dealer (East) always deals and takes the opening draw. */
export const DEALER: Seat = 0;

/** Concealed tiles a claiming seat contributes to a Pung (the discard is the third). */
export const PUNG_HAND_TILES = 2;

export type Phase = 'awaiting-discard' | 'claim-window' | 'hand-end';

export type HandResult =
  | { readonly kind: 'tsumo'; readonly winner: Seat; readonly winningTile: Tile }
  | { readonly kind: 'ron'; readonly winner: Seat; readonly winningTile: Tile; readonly from: Seat }
  | { readonly kind: 'exhausted' };

export interface MahjongState {
  readonly hands: readonly (readonly Tile[])[];
  readonly melds: readonly (readonly Meld[])[];
  readonly discards: readonly (readonly Tile[])[];
  readonly wall: readonly Tile[];
  readonly deadWall: readonly Tile[];
  readonly turn: Seat;
  readonly phase: Phase;
  readonly lastDiscard: { readonly tile: Tile; readonly from: Seat } | null;
  readonly drawnTile: Tile | null;
  readonly result: HandResult | null;
  readonly seed: number;
  readonly ruleset: Ruleset;
  /** Non-null only while a robbing-the-kong window is open — the seat whose added-kong declaration is pending. */
  readonly pendingAddedKongSeat: Seat | null;
}

export function nextSeat(seat: Seat): Seat {
  return ((seat + 1) % SEAT_COUNT) as Seat;
}

export function emptyPerSeat<T>(): T[][] {
  return Array.from({ length: SEAT_COUNT }, () => [] as T[]);
}

export function requirePhase(state: MahjongState, phase: Phase): void {
  if (state.phase !== phase) {
    throw new Error(`expected phase '${phase}', got '${state.phase}'`);
  }
}

export function requireDiscard(state: MahjongState): { readonly tile: Tile; readonly from: Seat } {
  if (state.lastDiscard === null) throw new Error('no discard to claim');
  return state.lastDiscard;
}

/**
 * Shared body for Pung/Chi/exposed-Kong: melds the discard, empties the
 * claim, hands the turn to `seat`. Used by `claimTransitions.ts` (Pung/Chi)
 * and `kongTransitions.ts` (exposed Kong) — a generic state-manipulation
 * helper, same category as the other functions in this module.
 */
export function applyClaim(
  state: MahjongState,
  seat: Seat,
  discardFrom: Seat,
  discardId: number,
  meld: Meld,
  usedIds: ReadonlySet<number>,
): MahjongState {
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === seat ? h.filter((t) => !usedIds.has(t.id)) : h)),
    melds: state.melds.map((m, i) => (i === seat ? [...m, meld] : m)),
    discards: state.discards.map((d, i) =>
      i === discardFrom ? d.filter((t) => t.id !== discardId) : d,
    ),
    turn: seat,
    phase: 'awaiting-discard',
    lastDiscard: null,
    drawnTile: null,
  };
}
